(function (root, factory) {
  const api = factory()

  if (typeof module === 'object' && module.exports) {
    module.exports = api
  }

  if (root) {
    root.DataRaporCalculatedText = api
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict'

  const FUNCTION_NAMES = new Set(['abs', 'min', 'max', 'round', 'pow', 'sqrt', 'floor', 'ceil'])
  const CONSTANTS = { PI: Math.PI, E: Math.E }
  const MAX_FORMULA_LENGTH = 2000

  function tokenize(formula) {
    const source = String(formula || '')
    if (!source.trim()) throw new Error('Formül boş olamaz')
    if (source.length > MAX_FORMULA_LENGTH) throw new Error('Formül çok uzun')

    const tokens = []
    let index = 0

    while (index < source.length) {
      const ch = source[index]
      if (/\s/.test(ch)) {
        index += 1
        continue
      }

      if (ch === '[') {
        const end = source.indexOf(']', index + 1)
        if (end === -1) throw new Error('Kapanmayan tag parantezi: [')
        const name = source.slice(index + 1, end).trim()
        if (!name) throw new Error('Tag adı boş olamaz')
        tokens.push({ type: 'identifier', value: name })
        index = end + 1
        continue
      }

      if (/\d|\./.test(ch)) {
        const match = source.slice(index).match(/^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+\-]?\d+)?/)
        if (!match) throw new Error('Geçersiz sayı')
        tokens.push({ type: 'number', value: Number(match[0]) })
        index += match[0].length
        continue
      }

      if (/[A-Za-z_]/.test(ch)) {
        const match = source.slice(index).match(/^[A-Za-z_][A-Za-z0-9_.:]*/)
        tokens.push({ type: 'identifier', value: match[0] })
        index += match[0].length
        continue
      }

      if ('+-*/%^(),'.includes(ch)) {
        tokens.push({ type: ch === '(' || ch === ')' || ch === ',' ? ch : 'operator', value: ch })
        index += 1
        continue
      }

      throw new Error(`Geçersiz karakter: ${ch}`)
    }

    tokens.push({ type: 'eof', value: '' })
    return tokens
  }

  function normalizeNumber(value, tagName) {
    if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'Value')) {
      value = value.Value
    }
    if (typeof value === 'boolean') return value ? 1 : 0
    if (typeof value === 'string') value = value.trim().replace(',', '.')
    const number = Number(value)
    if (!Number.isFinite(number)) throw new Error(`${tagName} için sayısal değer yok`)
    return number
  }

  function createResolver(values) {
    if (typeof values === 'function') return values
    if (values instanceof Map) {
      return name => values.has(name) ? values.get(name) : undefined
    }
    const source = values && typeof values === 'object' ? values : {}
    return name => Object.prototype.hasOwnProperty.call(source, name) ? source[name] : undefined
  }

  function evaluate(formula, values) {
    const tokens = tokenize(formula)
    const resolve = createResolver(values)
    let position = 0

    const peek = () => tokens[position]
    const take = () => tokens[position++]
    const expect = type => {
      const token = take()
      if (!token || token.type !== type) throw new Error(`Beklenen ifade: ${type}`)
      return token
    }

    function callFunction(name, args) {
      switch (name) {
        case 'abs': if (args.length !== 1) break; return Math.abs(args[0])
        case 'min': if (args.length < 1) break; return Math.min(...args)
        case 'max': if (args.length < 1) break; return Math.max(...args)
        case 'round':
          if (args.length === 1) return Math.round(args[0])
          if (args.length === 2 && Number.isInteger(args[1]) && args[1] >= 0 && args[1] <= 10) {
            const factor = 10 ** args[1]
            return Math.round((args[0] + Number.EPSILON) * factor) / factor
          }
          break
        case 'pow': if (args.length !== 2) break; return Math.pow(args[0], args[1])
        case 'sqrt': if (args.length !== 1) break; return Math.sqrt(args[0])
        case 'floor': if (args.length !== 1) break; return Math.floor(args[0])
        case 'ceil': if (args.length !== 1) break; return Math.ceil(args[0])
        default: break
      }
      throw new Error(`${name} fonksiyonu için parametre sayısı geçersiz`)
    }

    function parsePrimary() {
      const token = peek()
      if (token.type === 'number') {
        take()
        return token.value
      }
      if (token.type === '(') {
        take()
        const result = parseExpression()
        expect(')')
        return result
      }
      if (token.type === 'identifier') {
        take()
        const name = token.value
        if (peek().type === '(') {
          if (!FUNCTION_NAMES.has(name.toLowerCase())) throw new Error(`Bilinmeyen fonksiyon: ${name}`)
          take()
          const args = []
          if (peek().type !== ')') {
            do {
              args.push(parseExpression())
              if (peek().type !== ',') break
              take()
            } while (true)
          }
          expect(')')
          return callFunction(name.toLowerCase(), args)
        }
        if (Object.prototype.hasOwnProperty.call(CONSTANTS, name)) return CONSTANTS[name]
        const value = resolve(name)
        if (value === undefined || value === null || value === '') throw new Error(`${name} değeri henüz gelmedi`)
        return normalizeNumber(value, name)
      }
      throw new Error(`Beklenmeyen ifade: ${token.value || token.type}`)
    }

    function parseUnary() {
      if (peek().type === 'operator' && (peek().value === '+' || peek().value === '-')) {
        const operator = take().value
        const value = parseUnary()
        return operator === '-' ? -value : value
      }
      return parsePrimary()
    }

    function parsePower() {
      const left = parseUnary()
      if (peek().type === 'operator' && peek().value === '^') {
        take()
        return left ** parsePower()
      }
      return left
    }

    function parseTerm() {
      let result = parsePower()
      while (peek().type === 'operator' && ['*', '/', '%'].includes(peek().value)) {
        const operator = take().value
        const right = parsePower()
        if ((operator === '/' || operator === '%') && right === 0) throw new Error('Sıfıra bölme yapılamaz')
        if (operator === '*') result *= right
        else if (operator === '/') result /= right
        else result %= right
      }
      return result
    }

    function parseExpression() {
      let result = parseTerm()
      while (peek().type === 'operator' && (peek().value === '+' || peek().value === '-')) {
        const operator = take().value
        const right = parseTerm()
        result = operator === '+' ? result + right : result - right
      }
      return result
    }

    const result = parseExpression()
    if (peek().type !== 'eof') throw new Error(`Beklenmeyen ifade: ${peek().value}`)
    if (!Number.isFinite(result)) throw new Error('Hesap sonucu geçersiz')
    return result
  }

  function extractTagNames(formula) {
    if (!String(formula || '').trim()) return []
    const tokens = tokenize(formula)
    const names = []
    tokens.forEach((token, index) => {
      if (token.type !== 'identifier') return
      const next = tokens[index + 1]
      if (next && next.type === '(' && FUNCTION_NAMES.has(token.value.toLowerCase())) return
      if (Object.prototype.hasOwnProperty.call(CONSTANTS, token.value)) return
      if (!names.includes(token.value)) names.push(token.value)
    })
    return names
  }

  function validate(formula) {
    let tags = []
    try {
      tags = extractTagNames(formula)
      const values = Object.fromEntries(tags.map(tag => [tag, 1]))
      evaluate(formula, values)
      return { valid: true, tags }
    } catch (error) {
      // Matematiksel sonuç canlı değerlere bağlıdır; doğrulama burada yalnızca
      // sözdizimini denetler. Sıfıra bölme/sonsuz sonuç runtime'da gösterilir.
      if (error.message === 'Sıfıra bölme yapılamaz' || error.message === 'Hesap sonucu geçersiz') {
        return { valid: true, tags }
      }
      return { valid: false, tags: [], error: error.message }
    }
  }

  function format(value, precision, unit) {
    const digits = Math.max(0, Math.min(10, Number.parseInt(precision, 10) || 0))
    return `${Number(value).toFixed(digits)} ${String(unit || '').trim()}`.trim()
  }

  return { evaluate, extractTagNames, validate, format }
})
